import { analyticsService } from "@/common/services/analytics/analytics.service";
import { useSearchContext } from "@/features/search/context/SearchContext";

type SortOption = {
  id: string;
  sort: string | null;
  order: string | null;
  labelKey: string;
};

export const SORT_OPTIONS: SortOption[] = [
  {
    id: "relevance",
    sort: null,
    order: null,
    labelKey: "common:search_sort_most_relevant",
  },
  {
    id: "newest",
    sort: "created",
    order: "desc",
    labelKey: "common:search_sort_date_newest_first",
  },
  {
    id: "price-asc",
    sort: "price",
    order: "asc",
    labelKey: "common:search_sort_price_lowest_first",
  },
  {
    id: "price-desc",
    sort: "price",
    order: "desc",
    labelKey: "common:search_sort_price_highest_first",
  },
];

const idFor = (sort?: string, order?: string) =>
  SORT_OPTIONS.find(
    option =>
      (option.sort ?? undefined) === sort &&
      (option.order ?? undefined) === order
  )?.id ?? "relevance";

/**
 * The sort control's state, shared by the two things that render it: a select in the results row
 * from `md` up, and a radio group inside the mobile filter panel.
 *
 * `locked` is the service saying this page's order is not the shopper's to choose — "New in" and
 * "Most Popular" are an order, not a filtered set, and it keeps their sort whatever the URL says.
 * Callers render nothing at all then, rather than a control that does nothing.
 */
export const useSort = () => {
  const { route, navigation, query } = useSearchContext();

  const select = (id: string) => {
    const selected = SORT_OPTIONS.find(option => option.id === id);

    if (!selected) {
      return;
    }

    navigation.setSort(selected.sort, selected.order);

    /* `"created desc"`, `"price asc"` — the gateway PLP reported sort and order joined.
     * Relevance sets neither, so it reports as the option id instead of an empty string. */
    analyticsService.trackSortItemsList(
      selected.sort
        ? `${selected.sort} ${selected.order ?? ""}`.trim()
        : selected.id
    );
  };

  return {
    locked: query.data?.meta.rules.sortLocked ?? false,
    /* True only while a new result set is loading — what disables the control. */
    pending: query.isPlaceholderData,
    current: idFor(route.params.sort, route.params.order),
    select,
  };
};
